import type { FaqCategory } from '@/entities/board/api/board.api';
import type { BadgeProps } from '@/shared/components/ui/badge';

/**
 * FAQ 카테고리 — 레거시 `faq.js` CATEGORIES 색상 매핑.
 * blue→info, yellow→warning, green→success, purple→default, orange→outline
 */
export const FAQ_CATEGORIES: {
  value: FaqCategory | 'ALL';
  label: string;
  badge?: NonNullable<BadgeProps['variant']>;
}[] = [
  { value: 'ALL', label: '전체' },
  { value: 'USER', label: '회원', badge: 'info' },
  { value: 'PRESCRIPTION', label: '처방내역', badge: 'warning' },
  { value: 'SETTLEMENT', label: '정산', badge: 'success' },
  { value: 'DRUG_MATCHING', label: '맥스매치', badge: 'default' },
  { value: 'FILTERING', label: '필터링', badge: 'outline' },
];

export type FaqCategoryBadgeVariant = NonNullable<BadgeProps['variant']>;

export const getFaqCategoryLabel = (category: FaqCategory | string): string =>
  FAQ_CATEGORIES.find((c) => c.value === category)?.label ?? category;

export const getFaqCategoryBadgeVariant = (
  category?: FaqCategory | string | null,
): FaqCategoryBadgeVariant => {
  if (!category) return 'secondary';
  return (
    FAQ_CATEGORIES.find((c) => c.value === category)?.badge ?? 'secondary'
  );
};

/**
 * 공지 구분 — 레거시 `statusHelper.ANNOUNCEMENT_CATEGORY` + 신규 API 값 통합.
 * 매핑 없는 enum은 「기타」로 표시해 raw code가 테이블에 노출되지 않게 한다.
 */
export const ANNOUNCEMENT_CATEGORIES = [
  // 신규·현재 폼에서 사용
  { value: 'SERVICE_UPDATE', label: '서비스 업데이트', badge: 'warning' },
  { value: 'POLICY', label: '정책', badge: 'info' },
  { value: 'EVENT', label: '이벤트', badge: 'success' },
  { value: 'MAINTENANCE', label: '점검', badge: 'secondary' },
  { value: 'SCHEDULE_NOTICE', label: '일정 안내', badge: 'default' },
  // 레거시 API 잔존 값
  { value: 'DRUG_INFO', label: '의약품 정보', badge: 'info' },
  { value: 'EVENT_PROMOTION', label: '이벤트 프로모션', badge: 'success' },
  { value: 'IMPORTANT_NOTICE', label: '중요 공지', badge: 'destructive' },
  { value: 'UNKNOWN', label: '기타', badge: 'secondary' },
] as const;

export type AnnouncementCategoryBadgeVariant = NonNullable<
  BadgeProps['variant']
>;

export const SCHEDULE_NOTICE_CATEGORY = 'SCHEDULE_NOTICE' as const;

/** 폼·필터용 — 현재 등록 가능 카테고리 (레거시 잔존값 제외) */
export const ANNOUNCEMENT_FORM_CATEGORIES = ANNOUNCEMENT_CATEGORIES.filter(
  (c) =>
    [
      'SERVICE_UPDATE',
      'POLICY',
      'EVENT',
      'MAINTENANCE',
      'SCHEDULE_NOTICE',
    ].includes(c.value),
);

export const getAnnouncementCategoryLabel = (category?: string | null): string => {
  if (!category) return '기타';
  return (
    ANNOUNCEMENT_CATEGORIES.find((c) => c.value === category)?.label ?? '기타'
  );
};

export const getAnnouncementCategoryBadgeVariant = (
  category?: string | null,
): AnnouncementCategoryBadgeVariant => {
  if (!category) return 'secondary';
  return (
    ANNOUNCEMENT_CATEGORIES.find((c) => c.value === category)?.badge ??
    'secondary'
  );
};
